"use client";

import React, { useState } from 'react';
import { useToast } from '@/components/ui/Toast';

const commonErrors = [
    { pattern: /\b(their)\b\s+(is|was)/i, message: 'Check if you meant "there"', type: 'grammar' },
    { pattern: /\b(your)\b\s+(going|doing|welcome)/i, message: 'Check if you meant "you\'re"', type: 'grammar' },
    { pattern: /\b(its)\b\s+(a|an|the|very)/i, message: 'Check if you meant "it\'s"', type: 'grammar' },
    { pattern: / {2,}/, message: 'Double spaces detected', type: 'style' },
    { pattern: /[.?!]\s*[a-z]/, message: 'Sentence should start with a capital letter', type: 'grammar' },
    { pattern: /\b(teh)\b/i, message: 'Typo: "teh" should be "the"', type: 'spelling' },
    { pattern: /\b(recieve)\b/i, message: 'Typo: "recieve" should be "receive"', type: 'spelling' },
    { pattern: /\b(alot)\b/i, message: 'Typo: "alot" should be "a lot"', type: 'spelling' },
];

export default function GrammarCheckerClient() {
    const [text, setText] = useState('');
    const [issues, setIssues] = useState<{ message: string; type: string; index: number }[]>([]);
    const { showToast } = useToast();

    const checkGrammar = () => {
        if (!text.trim()) return;

        const foundIssues: { message: string; type: string; index: number }[] = [];

        commonErrors.forEach(rule => {
            let match;
            const regex = new RegExp(rule.pattern, 'gi');
            while ((match = regex.exec(text)) !== null) {
                foundIssues.push({
                    message: rule.message,
                    type: rule.type,
                    index: match.index
                });
            }
        });

        setIssues(foundIssues);
        if (foundIssues.length === 0) {
            showToast('No common errors found! Looking good.');
        } else {
            showToast(`Found ${foundIssues.length} potential issues.`, 'info');
        }
    };

    const clearText = () => {
        setText('');
        setIssues([]);
        showToast('Cleared', 'info');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Paste your text here to check for common grammar and spelling errors..."
                style={{
                    width: '100%',
                    height: '300px',
                    padding: '1rem',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    fontSize: '1.1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                }}
            />

            <div style={{ display: 'flex', gap: '1rem' }}>
                <button onClick={checkGrammar} className="btn-primary" style={{ flex: 1 }}>Check for Errors</button>
                <button onClick={clearText} style={{ padding: '0.75rem 1.5rem', background: '#ef4444', color: 'white', borderRadius: '8px', fontWeight: 600 }}>Clear</button>
            </div>

            {issues.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '1.5rem', color: '#1e293b' }}>Suggestions</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {issues.map((issue, idx) => (
                            <div key={idx} style={{
                                padding: '1rem',
                                borderLeft: `4px solid ${issue.type === 'grammar' ? '#3b82f6' : '#f59e0b'}`,
                                background: 'var(--accent)',
                                borderRadius: '0 8px 8px 0'
                            }}>
                                <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: 'var(--secondary)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                                    {issue.type} issue
                                </div>
                                <div style={{ fontSize: '1rem', color: '#334155' }}>{issue.message}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div style={{ marginTop: '2rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px', border: '1px solid var(--border)' }}>
                <h4 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Professional Tip:</h4>
                <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--secondary)' }}>
                    Always read your text out loud. Even the best automated checkers can miss context-specific nuances. Our tool focuses on high-impact common errors to help you polish your writing quickly.
                </p>
            </div>
        </div>
    );
}




